import { Clock, MapPin, Wrench, UserCheck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { IMG } from "../data/content";

const COMMITMENTS = [
  [Clock, "Prise en charge immédiate", "Notre service support est ouvert de 7h à 17h : votre appel est pris en charge tout de suite par un technicien.", "#45ADE0"],
  [MapPin, "Proximité", "Basés rue Jean Chalier, nous sommes chez vous en quelques minutes dans le Grand Nouméa.", "#E3B321"],
  [Wrench, "Sur site", "Nos techniciens se déplacent : pas de ticket anonyme, une vraie personne.", "#A6C46B"],
  [UserCheck, "Un interlocuteur", "Une seule équipe pour le réseau, la téléphonie, le cloud et le matériel.", "#EE8445"],
];

const TEAM = [
  ["N1", "Niveau 1", "Accueil du support, premier diagnostic et résolution des incidents courants.", "#45ADE0"],
  ["N2", "Niveau 2", "Interventions avancées sur les postes, serveurs, réseaux et la téléphonie, à distance ou sur site.", "#A6C46B"],
  ["N3", "Niveau 3", "Expertise : architecture, projets complexes, incidents critiques.", "#EE8445"],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero eyebrow="À propos" color="#A6C46B" title="Une équipe calédonienne, technique et réactive." lead="Créée à Nouméa en juin 2009, ENOVA est née d'une conviction simple : les entreprises de Nouvelle-Calédonie méritent un prestataire informatique qui décroche, qui se déplace et qui parle clairement." />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow>L'équipe</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-extrabold">10 techniciens et techniciennes, du niveau 1 au niveau 3.</h2>
          <p className="mt-4 text-ink/65">Chaque demande est prise en charge au bon niveau d'expertise, et transmise au niveau supérieur si nécessaire, sans que vous ayez à tout réexpliquer.</p>
          <img src={IMG.rackInstall} alt="Technicien en intervention" className="mt-8 w-full aspect-[4/3] object-cover" />
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 content-start" data-testid="about-team">
          <div className="sm:col-span-2 bg-ink text-white p-6 flex items-center gap-6" data-testid="about-team-count">
            <span className="font-display text-5xl font-extrabold">10</span>
            <span className="text-white/75">techniciens et techniciennes, réunis dans nos locaux de la rue Jean Chalier.</span>
          </div>
          {TEAM.map(([n, r, d, c]) => (
            <div key={r} data-testid={`about-team-level-${n.toLowerCase()}`} className={`bg-white border border-ink/10 p-6 ${n === "N3" ? "sm:col-span-2" : ""}`} style={{ borderTop: `4px solid ${c}` }}>
              <span className="font-display text-3xl font-extrabold" style={{ color: c }}>{n}</span>
              <h3 className="font-display text-lg font-bold mt-3">{r}</h3>
              <p className="text-sm text-ink/60 mt-1">{d}</p>
            </div>
          ))}
          <div className="sm:col-span-2 bg-white border border-ink/10 p-6" data-testid="about-zone">
            <h3 className="font-display text-lg font-bold">Zone d'intervention</h3>
            <p className="text-sm text-ink/65 mt-2">Sur site : Nouméa, Dumbéa, Mont-Dore, Païta. À distance : toute la Nouvelle-Calédonie. Brousse et îles : sur planification.</p>
          </div>
        </div>
      </section>
      <section className="bg-ink text-white py-20 lg:py-28 grain relative">
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Eyebrow color="#45ADE0" light>Nos engagements</Eyebrow>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="about-commitments">
            {COMMITMENTS.map(([Icon, t, d, c], i) => (
              <Reveal key={t} delay={i * 0.07} className="border-t-2 pt-6" style={{ borderColor: c }}>
                <Icon style={{ color: c }} />
                <h3 className="font-display text-xl font-bold mt-5">{t}</h3>
                <p className="text-sm text-white/60 mt-2">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <img src={IMG.noumea} alt="Nouméa, Nouvelle-Calédonie" className="w-full aspect-[4/3] object-cover" />
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6 space-y-5 text-ink/75 leading-relaxed">
          <Eyebrow color="#A6C46B">Notre histoire</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-extrabold text-ink">Depuis juin 2009 aux côtés des entreprises calédoniennes.</h2>
          <p>ENOVA a été créée en juin 2009 à Nouméa, d'un besoin du secteur minier : travailler autrement, ne plus intervenir uniquement dans l'urgence, mais exercer le métier du support de façon plus pertinente, plus efficace et moins énergivore pour les équipes.</p>
          <p>Depuis, l'entreprise accompagne les entreprises du territoire sur l'ensemble de leur système d'information : téléphonie, infogérance, réseau, cloud, conseil et matériel.</p>
          <p>Aujourd'hui, nous accompagnons des acteurs du secteur minier, des banques, des associations, des enseignes de commerce et des salles de sport, de Nouméa à Pouembout.</p>
        </Reveal>
      </section>
      <CtaBand color="#A6C46B" title="Faisons connaissance." text="Une question, un projet, l'envie d'échanger : notre équipe vous répond." action={["/contact", "Nous contacter"]} />
    </div>
  );
}
